import { inject, Injectable } from '@angular/core';
import { DashboardChartTypes } from '../enums/DashboardChartTypes';
import { ApiService } from '../../../../core/services';
import { Observable } from 'rxjs';
import { IDashboardChartConfig } from '../interfaces/IDashboardChartConfig';
import { IDashboard } from '../interfaces/IDashboard';
import { map } from 'rxjs/operators';
import { DashboardTypes } from '../enums/DashboardTypes';
import { HttpParams } from '@angular/common/http';

@Injectable({
  providedIn: 'root'
})
export class DashboardService {

  private readonly _basePath = '/tools';
  private readonly _defaultDashboardChartConfigs: IDashboardChartConfig[] = [
    {
      size: 12,
      order: 1,
      key: DashboardChartTypes.TSL,
      title: 'Heutige Verkäufe nach Standort',
      text: 'Hier sehen Sie die heutigen Verkaufszahlen aufgeschlüsselt nach Standort.',
      tooltipContent: 'Tagesaktuelle Verkäufe pro Standort.',
    },
    {
      size: 12,
      order: 2,
      key: DashboardChartTypes.NSD,
      title: 'Nächste Seminartermine',
      text: 'Hier sehen Sie die nächsten anstehenden Seminartermine.',
      tooltipContent: 'Übersicht der kommenden Seminartermine.',
    },
    {
      size: 6,
      order: 3,
      key: DashboardChartTypes.AQS,
      title: 'Artikel-Schnellsuche',
      text: 'Hier können Sie schnell nach Artikeln suchen – auch per EAN-Scan.',
      tooltipContent: 'Schnellsuche für Artikel per Suchfeld oder EAN-Scan.',
    },
    {
      size: 6,
      order: 4,
      key: DashboardChartTypes.TPP,
      title: 'Top 10 Produkte (letzte 30 Tage)',
      text: 'Hier sehen Sie die zehn meistverkauften Produkte der letzten 30 Tage.',
      tooltipContent: 'Top 10 Produkte nach Verkaufszahlen der letzten 30 Tage.',
    },
    {
      size: 12,
      order: 5,
      key: DashboardChartTypes.LPO,
      title: 'Letzte abgeschlossene Produktionsaufträge',
      text: 'Hier sehen Sie die zuletzt abgeschlossenen Produktionsaufträge.',
      tooltipContent: 'Übersicht der letzten abgeschlossenen Produktionsaufträge.',
    },
    {
      size: 12,
      order: 6,
      key: DashboardChartTypes.MSIVS,
      title: 'Inaktive Varianten mit Bestand (Lager Klosterlechfeld)',
      text: 'Hier sehen Sie alle inaktiven Varianten, die noch einen positiven Bestand im Lager Klosterlechfeld haben.',
      tooltipContent: 'Inaktive Varianten mit positivem Lagerbestand.',
    },
    {
      size: 12,
      order: 7,
      key: DashboardChartTypes.SCN,
      title: 'Scanner',
      text: 'Scanner für Dokumente und Gutscheine.',
      tooltipContent: 'Dokumente scannen (→ Inbox) oder Gutscheine scannen (→ Marketing Gutscheine).',
    },
    {
      size: 12,
      order: 8,
      key: DashboardChartTypes.LRC,
      title: 'Letzte Reklamationen',
      text: 'Hier sehen Sie die zuletzt eingegangenen Reklamationen.',
      tooltipContent: 'Übersicht der letzten Reklamationen.',
    },
    {
      size: 6,
      order: 9,
      key: DashboardChartTypes.RCF,
      title: 'Rezeptkarten-Finder',
      text: 'Hier können Sie einen Artikel scannen und die passende Rezeptkarte finden.',
      tooltipContent: 'Artikel scannen oder EAN eingeben, um Rezeptkarten zu finden.',
    },
    {
      size: 12,
      order: 10,
      key: DashboardChartTypes.MNS,
      title: 'Meine nächsten Schritte',
      text: 'Hier sehen Sie die für Sie vorgesehenen offenen Schritte.',
      tooltipContent: 'Offene Schritte, die für Sie vorgesehen sind.',
    },
    {
      size: 12,
      order: 11,
      key: DashboardChartTypes.OSP,
      title: 'Offene Schritte (alle Produkte)',
      text: 'Hier sehen Sie, wo eine Neuanlage gerade hängt.',
      tooltipContent: 'Nächster offener Schritt je Produkt, längste Wartezeit zuerst.',
    },
  ];

  private readonly _apiService = inject(ApiService);

  public getDashboard(): Observable<IDashboard> {
    const params = new HttpParams().append('dashboardType', DashboardTypes.MAIN);

    return this._apiService.get(`${ this._basePath }/get-dashboard`, params).pipe(
      map((dashboard: IDashboard) => {

        if (!dashboard.dashboard) {
          dashboard.dashboard = this._defaultDashboardChartConfigs;
        } else {
          dashboard.dashboard = JSON.parse(dashboard.dashboard as unknown as string) as IDashboardChartConfig[];
          dashboard.dashboard = dashboard.dashboard.filter(config => {
            return this._defaultDashboardChartConfigs.find(x => x.key === config.key);
          });

          const newChartConfigs = this._defaultDashboardChartConfigs
            .filter(config => !(dashboard.dashboard as IDashboardChartConfig[]).find(x => x.key === config.key))
            .map((config, index) => {
              config.order = dashboard.dashboard.length + index + 1;

              return config;
            });

          dashboard.dashboard.push(...newChartConfigs);

          dashboard.dashboard = dashboard.dashboard
            .sortByKey('order')
            .map((config, index) => {
              const { title, text, tooltipContent } = this._defaultDashboardChartConfigs.find(x => x.key === config.key);

              config.order = index + 1;
              config.title = title;
              config.text = text;
              config.tooltipContent = tooltipContent;

              return config;
            });
        }

        return dashboard;
      })
    );
  }

  public saveDashboard(dashboardChartConfigs: IDashboardChartConfig[]): Observable<void> {
    const config = dashboardChartConfigs.map(({ size, order, key, isVisible }) => {
      return {
        size,
        order,
        key,
        isVisible
      };
    });

    const dashboard: IDashboard = {
      type: DashboardTypes.MAIN,
      dashboard: JSON.stringify(config) as unknown as IDashboardChartConfig[]
    };

    return this._apiService.post(`${ this._basePath }/save-dashboard`, dashboard);
  }
}
